/**
 * 입고작업 2.0(GL17) 상수 — 1.0 GL09 (`GL09.js`·`GLTemplate.js`·`store/modules/grid.js`) 값 그대로.
 */
import type { PrinterConfig } from '@/utils/qzTray/qzTrayClient'
import type { ScanCommandType, ScanResultType } from './queries/types'

/** 스캔·첨부 저장 API 가 서버에서 고정하는 화주(BE `MUSINSA_GLOBAL_SHIPPER_CODE`). 공통 파일 업로드도 이 화주로 올린다. */
export const GLOBAL_INBOUND_SHIPPER = 'MUSINSAGLOBAL'

/** 이 화면의 메뉴코드 — 공통 파일 API 감사 헤더(X-Menu-Code)에 싣는다. */
export const INBOUND_WORK_MENU_CODE = 'GL17'

/** 첨부 완료 한 번에 보낼 수 있는 최대 건수 — BE `GlobalInboundAttachmentRequest.MAX_FILES`. */
export const INBOUND_ATTACHMENT_MAX = 10

/** 송장 최소 길이(1.0 `value.length < 7`, BE `@Size(min = 7)`). */
export const MIN_INVOICE_LENGTH = 7

/** 특수 바코드 앞뒤 표식(1.0 `!@#…$%^`). */
export const COMMAND_PREFIX = '!@#'
export const COMMAND_SUFFIX = '$%^'

/** 특수 바코드 토큰 → 2.0 scan 명령(1.0 grid.js `GL09_SCAN_COMMANDS` — 순서대로 먼저 맞는 것). */
export const SCAN_COMMANDS: ReadonlyArray<readonly [token: string, command: ScanCommandType]> = [
  ['QTY', 'QUANTITY_MISMATCH'],
  ['VARIANT', 'VARIANT'],
  ['RESET', 'RESET'],
  ['REPRINT', 'REPRINT'],
]

/** 같은 송장 재스캔 차단 기준 상태(1.0 `ORD_STATUS === '입고요청'`). */
export const INBOUND_REQUESTED_STATUS = '입고요청'

/** 녹화·해상도 기본값(1.0 `rowAddData` 초기값). */
export const DEFAULT_RECORD_SECONDS = '0'
export const DEFAULT_RESOLUTION_WIDTH = '1280'
export const DEFAULT_RESOLUTION_HEIGHT = '720'

/** 라벨 주문일련번호 — 한 줄 3개, 최대 10개까지 찍고 나머지는 "외 n건"(1.0 GLTemplate). */
export const ORDER_OPTS_PER_LINE = 3
export const ORDER_OPTS_MAX = 10

/** 글로벌 라벨 일본 규격 시작 시각(1.0 GLTemplate `isJPSize` — 24/05/07 PM 12:30 부터). */
export const JP_LABEL_SIZE_SINCE = '2024-05-07 12:29:59'

/** 라벨 프린터(1.0 GLTemplate `printStart`). 높이·방향은 일본 규격 여부로 정한다(`label/inboundLabel.ts`). */
export const LABEL_PRINTER_BASE: Pick<PrinterConfig, 'printerName' | 'width' | 'units' | 'rasterize'> = {
  printerName: 'BIXOLON SLP-DL410',
  width: 10,
  units: 'cm',
  rasterize: false,
}
export const LABEL_HEIGHT_JP = 15
export const LABEL_HEIGHT_DEFAULT = 8

/** 판정 결과 표기(BE `ScanResultType.remark` — MATCHED 는 remark 가 비어 '정상'). */
/**
 * 라벨이 임의 합포건을 가르는 서버 안내 문구 조각. 1.0 GLTemplate 이 응답 문구로 갈랐고 라벨 분기를 1.0 과 같게 두려고 그대로 쓴다.
 * 서버 문구가 바뀌면 함께 고친다(2.0 응답 scanResult=MULTI_CONSOLIDATION 과 같은 경우다).
 */
export const MULTI_CONSOLIDATION_MESSAGE = '임의 합포건'

/** 판정 문구 — ScanResultType 전부를 덮는다(새 판정이 생기면 컴파일이 막는다). */
export const SCAN_RESULT_LABEL: Record<ScanResultType, string> = {
  MATCHED: '정상',
  NOT_FOUND: '송장 미매칭',
  MULTI_CONSOLIDATION: '임의 합포건',
  DUPLICATED: '중복 처리건',
  QUANTITY_MISMATCH: '수량 상이',
  VARIANT: '이형',
  RESET: '초기화',
  REPRINT: '재출력',
  TO_BE_BLOCKED: 'TO-BE 검수 대상',
}

/** 안내 문구 — 1.0 alert 문구를 그대로 쓰고(띄어쓰기만 다듬음) 2.0 에서 새로 생긴 안내를 더했다. */
export const MESSAGES = {
  scanPrompt: '송장을 스캔해 주세요.',
  emptyInvoice: '송장을 입력해주세요',
  forbiddenChar: '\\ , ￦ 문자는 입력할 수 없습니다.',
  tooShort: `송장은 ${MIN_INVOICE_LENGTH}자리 이상 입력해주세요.`,
  noOrgInvoice: '원본송장이 존재하지 않습니다.',
  alreadyInbound: '이미 입고 처리된 송장입니다.',
  scanBusy: '앞 송장을 처리하고 있습니다. 잠시 뒤 다시 스캔해 주세요.',
  scanEmpty: '스캔 응답이 비어 있습니다.',
  scanFailed: '스캔 처리 중 오류가 발생했습니다.',
  scanNotFound: '송장을 찾을 수 없습니다.',
  scanDone: (invoice: string) => `${invoice} 처리했습니다.`,
  printFailed: '라벨을 인쇄하지 못했습니다. QZ Tray 실행과 프린터 연결을 확인해 주세요.',
  cameraUnavailable: '카메라를 사용할 수 없습니다',
  cameraNotReady: '카메라가 준비되지 않았습니다.',
  cameraReady: '카메라 대기',
  cameraConnecting: '카메라 연결 중',
  orgInvoice: (invoice: string) => `원본송장 ${invoice}`,
  orgInvoiceEmpty: '원본송장 없음',
  /** 녹화 카드 파일 목록에서 파일명 뒤에 붙는 상태 */
  attachUploadingSuffix: ' (업로드 중)',
  attachFailedSuffix: ' (실패)',
  alreadyRecording: '이미 녹화 중입니다.',
  notRecording: '녹화 중이 아닙니다.',
  recordStopped: '녹화를 멈췄습니다. 영상을 올립니다.',
  uploadFailed: (fileName: string, reason: string) => `첨부 업로드 중 에러가 발생했습니다 (${fileName}, ${reason})`,
  saveNoOrgInvoice: '저장 데이터가 존재하지 않습니다.',
  saveWhileRecording: '녹화 중입니다. 녹화를 멈춘 뒤 저장해 주세요.',
  saveWhileUploading: '첨부가 모두 올라갈 때까지 잠시만 기다려 주세요.',
  saveNoAttachment: '저장할 첨부가 없습니다. 사진을 찍거나 녹화한 뒤 저장해 주세요.',
  saveFailed: '첨부를 저장하지 못했습니다.',
  saveNoHistory: (invoice: string) => `원본송장 ${invoice} 의 스캔 이력이 없어 첨부를 연결하지 못했습니다.`,
  saveDone: (count: number, invoice: string) => `첨부 ${count}건을 원본송장 ${invoice} 에 저장했습니다.`,
  saveTooMany: (count: number) => `첨부는 한 번에 ${INBOUND_ATTACHMENT_MAX}건까지 저장할 수 있습니다. 지금 ${count}건입니다.`,
  saveTruncated: (count: number, invoice: string) =>
    `첨부 ${count}건을 저장했지만 원본송장 ${invoice} 의 첨부 칸(20자)이 넘쳐 일부는 이력에 연결되지 않았습니다.`,
} as const
